/*
 * Copyright (c) 2023-2024 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { camelToTitleCase } from "../util/ConvertCase";
import { gdprCompliant } from '../util/CountryList';
import { validateFileSize } from "../util/File";
import { useLocalTimezone } from "../util/TimezoneList";
import debug from "../util/constants/Debug";

export const acceptImage = "image/png,image/jpeg";
export const acceptAudio = "audio/mp3";
export const acceptVideo = "video/mp4,image/gif";
export const acceptIco = "ico";
export const acceptMedia = `${acceptImage},${acceptVideo}`;
export const acceptAll = `${acceptImage},${acceptAudio},${acceptVideo}`;

const notEmpty = (value) => value ? value : null;

const getHierarchyAttributes = (item, forceAddress = false) => {
  if (item == null) {
    return {};
  }

  // return all info each level of the hierarchy
  const address = item.address;
  const addressAvailable = address != null;
  const localTimezone = address?.timezone === useLocalTimezone;

  return {
    email: notEmpty(item.contact?.email),
    phone_number: notEmpty(item.contact?.phoneNumber),
    website_url: notEmpty(item.contact?.website),
    req_email_validation: notEmpty(item.hotspotConfig?.rev),
    https_hotspot_enabled: notEmpty(item.hotspotConfig?.hhe),
    hotspot_redirect_url: notEmpty(item.hotspotConfig?.hre),
    router_idle_timeout: notEmpty(item.hotspotConfig?.rit),
    address: forceAddress || addressAvailable ? {
      street: notEmpty(address.street),
      city: notEmpty(address.city),
      state: notEmpty(address.state),
      zip: notEmpty(address.zip),
      country: notEmpty(address.country),
      gdpr_compliant: gdprCompliant(address.country),
      timezone: localTimezone ? null : address.timezone,
      use_local_timezone: localTimezone,
    } : undefined
  };
};

const getGoogleReviewAttributes = (googleReview) => {
  return {
    google_review_search: googleReview.search,
    google_review_data_id: googleReview.dataId,
    google_review_place_id: googleReview.placeId,
  };
};

export const validateMediaFileSize = (fileList, megabytes = 50) => validateFileSize(fileList, 1048576 * megabytes) ? true : `Files cannot be larger than ${megabytes} MB`

export const provisioningFormToPayload = (data) => {
  // top-level data variables
  const client = data.business;
  const locations = data.location.general?.name ? [data.location] : [];
  const routers = data.router.general?.mac ? [data.router] : [];
  const accounts = data.account.login?.email ? [data.account] : [];
  const googleReview = data.googleReview;

  debug.log(data);
  
  // prepare payload
  const payload = {
    id: "1",
    name: client.general.name,
    vat_tax_id: client.general.taxId,
    brand_id: client.general.brandId,
    ...getHierarchyAttributes(client),
    locations: locations.map((item, i) => {
      return {
        id: i.toString(),
        name: item.general.name,
        description: item.general.description,
        google_review_search: item.googleReviewSearch,
        ...getHierarchyAttributes(item, true),
        ...getGoogleReviewAttributes(googleReview),
      };
    }),
    nas_client_info: routers.map((item, i) => {
      return {
        id: i.toString(),
        mac_address: item.general.mac,
        ssid: item.general.ssid,
        description: item.general.description,
        location_id: locations.findIndex(location => item.location === location.name).toString(),
        ...getHierarchyAttributes(item),
      };
    }),
    accounts: accounts.map((item) => {
      return {
        username: item.login.email,
        password: item.login.password,
        permission_edit_templates: item.permissions.editTemplates,
        permission_edit_layouts: item.permissions.editLayouts,
        first_name: notEmpty(item.general.firstName),
        last_name: notEmpty(item.general.lastName),
      };
    }),
  };

  // prepare image data
  const images = []
  const imageTypeList = [
    { id: "l", type: "logo" },
    { id: "s", type: "splashScreen" },
  ];
  const imageCheckList = [
    { id: "b", description: "Business", objects: [client] },
    { id: "l", description: "Location", objects: locations },
    { id: "r", description: "Router", objects: routers },
  ];

  // loop over each client, location, and router
  imageCheckList.forEach((item) => {
    const { id: objId, description, objects } = item;
    objects.forEach((object) => {

      // loop over image types (logo and splash screen)
      imageTypeList.forEach((imageType) => {
        const { id: typeId, type } = imageType;

        // check if image exists
        if (object.image?.[type]?.length) {

          // create image container
          const imageContainer = {}
          imageTypeList.forEach((item) => imageContainer[item.type] = []);

          // add image to container
          const image = object.image[type][0];
          imageContainer[type].push({
            id: `${objId}-${typeId}-${object.general?.mac ?? object.general?.name}`,
            image: image,
            name: image.name,
            description: `${description} ${camelToTitleCase(type)}`
          });

          // add container to image list
          images.push(imageContainer);
        }
      });
    });
  });

  // return data
  return { payload, images };
};

export const clientImageDataToPayload = (imageData, imageIndex) => {
  // check null
  if (imageData == null ||
      imageData.images == null ||
      imageData.images.length === 0 ||
      imageData.client == null)
  {
    return {};
  }

  // prepare to upload images
  const toImages = (list) => {
    return list.map((item) => ({
      id: item.id,
      name: item.name,
      description: item.description,
      key: item.id
    }));
  };

  const logoList = imageData.images[imageIndex].logo;
  const splashScreenList = imageData.images[imageIndex].splashScreen;
  const imageList = [...logoList, ...splashScreenList];

  const logos = toImages(logoList);
  const splashScreens = toImages(splashScreenList);

  const newClient = imageData.client;
  const newLocations = newClient.locations;
  const newRouters = [];

  newLocations.forEach((location) => {
    location.routers.forEach((router) => {
      newRouters.push(router);
    })
  });

  // C/L/R associations
  const associations = [];

  imageList.forEach((item) => {
    const type = item.id[0];
    const name = item.id.slice(4)
    let key = null
    let value = null;

    switch (type) {
      case 'b':
        key = "client_id";
        value = newClient.id;
        break;
      case 'l':
        key = "location_id";
        value = newLocations.find((location) => location.name === name)?.id;
        break;
      case 'r':
        key = "router_id";
        value = newRouters.find((router) => router.macAddress === name)?.id;
        break;
      default:
        debug.warn("Invalid image id type");
        break;
    }

    if (!key || !value) {
      return;
    }

    associations.push({
      id: item.id,
      [key]: value
    });
  });

  // prepare form data
  const formData = new FormData();
  const fileList = [];

  formData.append("client_id", newClient.id);
  formData.append("logos", JSON.stringify(logos));
  formData.append("splash_screens", JSON.stringify(splashScreens));
  formData.append("associations", JSON.stringify(associations));
  imageList.forEach((item) => {
    formData.append(item.id, item.image);
    fileList.push(item);
  });

  // return data
  return { formData, fileList };
};
